import {html, css, LitElement} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {gsap} from 'gsap';
import Draggable from 'gsap/Draggable';
import {property} from 'lit/decorators/property.js';
import {when} from 'lit/directives/when.js';
import {connect} from 'pwa-helpers';
import {setDimmLevel} from '../../slices/configurationSlice';
import {decision} from '../../slices/decisionsSlice';
import store from '../../store';
import {
	selectSystemLuminousFluxMax,
	selectSystemLuminousFluxMin,
	selectAvailableDimmingLevel,
	selectCoefficients, selectDimmLevel
} from '../../selectors';
import radio from '../../../icons/radio';
import {getValueOfFeature, hasFeatureValue, isDecided} from '../../util/values';

gsap.registerPlugin(Draggable);

/**
 * Configuration values
 * @property {number} selectedNumber - Default number on the slider used for power calculation and data display.
 * @property {number} defaultNumber - Default number on the slider used for slider thumb onload display.
 * @property {Array<Object>} numbers - Combined array of major and intermediate numbers, used internally for rendering the list.
 * @property {number} fixedHeightPerNumber - Fixed height for each number element, used for scrolling to a specific number.
 * @property {Object} title - An object containing the title in different languages.
 * @property {Function} isSatisfied - A function that returns a boolean, used for passing satisfied state to the widget from section config.
 * @property {type: String} action - An action to be dispatched when the selected number changes.
 */


/**
 * @classdesc Custom Vertical Slider creates a vertical range slider with major and intermediate numerical values, obtained from backend.
 * Interaction possible with clicking on values or dragging to a specific position.
 * @version 1.0.0
 * @path
 */
@customElement('vertical-slider')
export class VerticalSlider extends connect(store)(LitElement) {

	static styles = css`
		.arrow-box {
			background: var(--color-white);
			clip-path: polygon(15% 0%, 100% 0%, 100% 100%, 15% 100%, 0% 50%);
			height: 25px;
			font-size: var(--font-size-widget-small);
			color: var(--color-black);
			font-family: var(--font-family-light), sans-serif;
			left: calc(var(--vertical-slider-width) - var(--vertical-slider-arrow-width));
			position: absolute;
			right: auto;
			width: 65px;
			z-index: 9;
		}

		.selected-number-display {
			position: absolute;
			top: 5px;
			bottom: 5px;
			left: 15px;
			margin: 0;
			right: auto;
		}

		.data {
			font-family: var(--font-family-regular), sans-serif;
			font-size: var(--font-size-widget-small-mono);
		}

		.data p:first-child {
			margin-top: 0;
		}

		.data span {
			font-family: var(--font-family-bold), sans-serif;
			font-size: var(--font-size-widget-small-mono);
		}

		.description {
			align-self: start;
			color: var(--color-white);
			display: flex;
			height: 100%;
			flex-direction: column;
			font-family: var(--font-family-light), sans-serif;
			font-size: var(--font-size-widget-small-mono);
			@media (max-width: 768px) {
				margin-top: calc(2 * var(--sections-gap));
			}
		}

		.description-text {
			margin-top: auto;
		}

		.description-text p {
			margin-bottom: 0;
		}

		.dimm-level {
			position: relative;
		}

		.icon {
			width: 11px;
			height: 11px;
			padding: 5px;
			display: inline-block;
		}

		.icon svg {
			display: block;
			fill: currentColor
		}

		.icon.satisfied {
			color: var(--color-grey5);
		}

		.intermediate-number {
			color: transparent;
			cursor: pointer;
			height: var(--vertical-slider-number-element-height);
		}

		.intermediate-number::before {
			content: "-";
			color: var(--color-widget-border);
			cursor: pointer;
			line-height: var(--vertical-slider-number-element-height);
			visibility: visible;
		}

		.major-number {
			color: var(--color-widget-text);
			cursor: pointer;
			height: var(--vertical-slider-number-element-height);
			padding-left: var(--vertical-slider-major-numbers-left-padding);
			position: relative;
			line-height: 1;
		}

		.major-number::before {
			background-color: var(--color-widget-border);
			content: "";
			display: inline-block;
			height: 1px;
			left: 0;
			position: absolute;
			top: 50%;
			transform: translateY(-50%);
			width: var(--vertical-slider-major-numbers-left-indicator);
		}

		.notSatisfied {
			color: var(--color-warn);
		}

		.number-display span {
			position: absolute;
			right: 5px;
			left: auto;
			top: 5px;
			bottom: 5px;
		}

		.numbers {
			font-family: var(--font-family-light);
			font-size: var(--vertical-slider-number-element-height);
			list-style-type: none;
			margin: 0;
			padding-bottom: var(--vertical-slider-numbers-list-padding);
			padding-left: 0;
			padding-top: var(--vertical-slider-numbers-list-padding);
		}

		.range-slider-wrapper {
			align-items: baseline;
			display: grid;
			gap: calc(1.6 * var(--sections-gap));
			grid-template-columns: 1fr 3fr;
			max-width: var(--widget-max-width);
			@media (max-width: 768px) {
				display: block;
			}
		}

		.satisfied {
			color: var(--color-text-widget-title);
		}

		.title {
			display: flex;
			justify-content: space-between;
			align-items: center;
			font: var(--font-widget-title);
			color: var(--color-widget-text);
			margin-bottom: 12px;
		}

		#vertical-slider {
			background-color: transparent;
			border: 1px solid var(--color-widget-border);
			min-height: var(--vertical-slider-min-height);
			overflow-y: auto;
			position: relative;
			width: var(--vertical-slider-width);

			-ms-overflow-style: none;
			scrollbar-width: none;
			/* Hide scrollbar for Chrome, Safari and Opera */

			&::-webkit-scrollbar {
				display: none;
			}
		}
	`;

	@property({type: Number})
	selectedNumber = null;

	@property({type: Number})
	defaultNumber = null;

	@property({type: Object})
	title = {
		de: '[Titel]',
		en: '[Title]',
		fr: '[Title]'
	};

	@property({type: Function})
	isSatisfied = null;

	@property({type: String})
	action = '';

	@property({type: Array})
	numbers = [];

	@property({type: Object})
	coefficients = null;

	fixedHeightPerNumber = 12;

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	connectedCallback() {
		super.connectedCallback();
	}

	/**
	 * Responds to changes in the Redux store state. Updates the component's internal state based on these changes.
	 *
	 * This method is called whenever there is a change in the Redux store. It updates language settings,
	 * major and intermediate numbers for the slider. It also sets default values for the slider based on
	 * the number of items available.
	 *
	 * If there's only one number in the slider, it automatically sets that number as the selected value.
	 * This method also reinitializes the GSAP Draggable elements whenever there's an update to the numbers.
	 *
	 * @param {Object} state - The current state of the Redux store.
	 */

	stateChanged(state) {
		this.lang = state.status.lang;
		const max = selectSystemLuminousFluxMax(state);
		const min = selectSystemLuminousFluxMin(state);
		const availableDimmingLevel = selectAvailableDimmingLevel(state);
		const newNumbers = [];
		availableDimmingLevel.forEach(level => {
			level.steps.forEach(step => {
				newNumbers.push({value: step, type: step !== level.value?'intermediate':'major'});
			});
		});
		this.coefficients = selectCoefficients(state);
		if (this.shouldUpdate(newNumbers)) {
			if (selectDimmLevel(state) !== null) {
				this.selectedNumber = selectDimmLevel(state);
			} else {
				if (isDecided('LE_DimmingLevel_1', state.decisions.data)) {
					this.selectedNumber = parseInt(getValueOfFeature('LE_DimmingLevel_1', state.decisions.data), 10);
				}
			}
			this.numbers = newNumbers;
			this.updateSliderElements();
			this.updateSliderPosition();
		}
	}

	// compare current and new numbers, return true if different
	shouldUpdate(newNumbers)  {
		return JSON.stringify(this.numbers) !== JSON.stringify(newNumbers);
	}
	/**
	 * Responds to updates in the component's properties. Specifically, it handles updates to the 'numbers' property.
	 *
	 * This lifecycle method is part of the LitElement framework and is called after the component's properties have been updated.
	 * If the 'numbers' property has changed, this method reinitializes the GSAP Draggable elements to reflect the new data.
	 * It ensures that any existing Draggable instance is destroyed before creating a new one, preventing memory leaks and
	 * ensuring that the slider interacts correctly with the updated data.
	 *
	 * @param {Map} changedProperties - A map of changed properties where keys are the property name and values are the old values.
	 */
	xupdated(changedProperties) {
		if (changedProperties.has('numbers')) {
			this.updateSliderElements();
			if (this.draggableInstance) {
				this.draggableInstance.kill();
			}
			this.updateSliderElements();
		}
	}


	/**
	 * Lifecycle callback that is called after the component's template has been rendered to the DOM.
	 * Initializes properties and sets up the Draggable instance for the arrow box.
	 * @method firstUpdated
	 * @instance
	 * @memberof VerticalSlider
	 * @returns {void}
	 */
	firstUpdated() {
		super.firstUpdated();
		this.updateSliderElements();
	}

	// DONE: extracted in it own method
	updateSliderElements() {
		if (this.numbers.length === 0) return; // return if no numbers are available
		console.log('updateSliderElements', this.numbers, this.coefficients);

		const verticalSlider = this.shadowRoot.getElementById('vertical-slider');
		const arrowBox = this.shadowRoot.querySelector('.arrow-box');
		const numbersList = this.shadowRoot.querySelector('.numbers');
		const totalHeight = numbersList?.offsetHeight ?? 300;
		const heightPerNumber = totalHeight / this.numbers.length;
		const points = this.numbers.map((_, index) => {
			const y = index * heightPerNumber;
			return {x: 0, y: y};
		});

		// Remove old draggable (if it exist) and create the new one or update values
		if (this.draggableInstance) {
			this.draggableInstance.kill();
		}
		const draggables = Draggable.create(arrowBox, {
			type: 'y',
			bounds: verticalSlider,
			liveSnap: {
				points: points,
				radius: 5,
			},
			onDrag: function () {
				const scrollPercentage = this.y / verticalSlider.offsetHeight;
				verticalSlider.scrollTop =
					scrollPercentage * (totalHeight - verticalSlider.offsetHeight);
				// DONE: show the "current" number base on position
				this.updateSelectedNumber(false);
			},
			callbackScope: this, // Setting the callback scope to the component
			onDragEnd: function () {
				// DONE: Extract mathod to calculate position and assign to selectedNumber
				this.updateSelectedNumber(true);
			},
		});
		this.draggableInstance = draggables[0];
	}

	/**
	 * Renders the list of numbers (both major and intermediate) as HTML elements.
	 * Each number is represented as a list item with a class indicating its type.
	 *
	 * @returns {TemplateResult} A LitElement template result containing the rendered list of numbers.
	 */
	renderNumbers() {
		return this.numbers.map((numberObj) => {
			const className =
				numberObj.type === 'major' ? 'major-number' : 'intermediate-number';
			return html`
				<li class='${className}' @click='${(e) => this.handleNumberClick(e, numberObj.value)}'>
					${numberObj.value}
				</li>`;
		});
	}

	/**
	 * Handles click events on the numbers in the range slider.
	 * Calculates the new position for the arrow box based on the clicked number
	 * and animates the arrow box to that position.
	 *
	 * @param {Event} e - The click event object.
	 * @param {number} number - The number that was clicked.
	 */
	handleNumberClick(e, number) {
		this.selectedNumber = number;
		this.updateSliderPosition();
		this.dispatchStatusEvents();
	}

	updateSliderPosition() {
		const arrowBox = this.shadowRoot.querySelectorAll('.arrow-box');

		// Using the fixed height for each number to ensure correct positioning
		const index = this.numbers.findIndex(n => n.value === this.selectedNumber);
		const yPosition = index * this.fixedHeightPerNumber;

		// Scroll the list to the selected number and update the selected number displayed in the arrow box
		gsap.to(arrowBox, {
			y: yPosition,
			duration: 0.5,
			onComplete: () => {
			}
		});
	}

	/**
	 * Updates and displays the selected number based on the position of the arrow box.
	 * if dispatch is true, also dispatches an action to update the dimmLevel state.
	 * Called onDragEnd and when clicked
	 *
	 * @param {boolean} dispatch - dispatch redux events
	 * @memberof VerticalSlider
	 */
	updateSelectedNumber(dispatch = false) {
		const arrowBox = this.shadowRoot.querySelector('.arrow-box');
		const numbersList = this.shadowRoot.querySelector('.numbers');
		const totalHeight = numbersList.offsetHeight;
		const heightPerNumber = totalHeight / this.numbers.length;
		const arrowBoxTransform = arrowBox.style.transform;
		const yPosition = arrowBoxTransform ? parseFloat(arrowBoxTransform.match(/translate3d\(0px, (\d+(\.\d+)?)px, 0px\)/)[1]) : 0;
		const index = Math.ceil(yPosition / heightPerNumber);

		this.selectedNumber = this.numbers[index].value;
		console.log('updateSelectedNumber', dispatch, this.selectedNumber);

		if (dispatch) {
			this.dispatchStatusEvents();
		}
	}

	/**
	 * dispatches actions to update the dimmLevel state and the decision
	 * dimmlevel is set on status
	 * decision will be send for the next major number in the list
	 */
	dispatchStatusEvents() {
		console.log('dispatchStatusEvents', this.selectedNumber);
		store.dispatch(setDimmLevel(this.selectedNumber));

		// find the next major number in this.numbers that is larger then this.selectedNumber
		const nextMajorNumber = this.numbers.find((number) => number.value >= this.selectedNumber && number.type === 'major');


		store.dispatch(decision({
			variableId: 'LE_DimmingLevel_1',
			featureValueId: nextMajorNumber.value,
			state: 'Included'
		}));
	}

	/**
	 * Calculates the power based on the selected number.
	 * The power is calculated using a formula with coefficients a, b, c and d.
	 * 	//returns power * 0.1; // using formualar a + b * lum + c * lum ^ 2 + d * lum ^ 3
	 *
	 * @param {number} lum - The selected number from the slider, used as an input for the power calculation.
	 * @return {number} - The calculated power value, rounded to the nearest integer.
	 */
	calculatePower(lum) {
		// TODO: use formular type
		const c = this.coefficients.formular2.coefficients;
		const power = c[0] +  c[1] * lum + c[2] * Math.pow(lum, 2); // + c[4] * Math.pow(lum, 3);
		return Math.round(power * 100)/100;
	}
	/**
	 * Calculates the power based on the selected number.
	 * The power is calculated using a formula with coefficients a, b, c and d.
	 * 	//returns power * 0.1; // using formualar a + b * lum + c * lum ^ 2 + d * lum ^ 3
	 *
	 * @param {number} lum - The selected number from the slider, used as an input for the power calculation.
	 * @return {number} - The calculated power value, rounded to the nearest integer.
	 */
	calculateCurrent(lum) {
		// TODO: use formular type
		const c = this.coefficients.formular1.coefficients;
		const power = c[0] +  c[1] * lum + c[2] * Math.pow(lum, 2); // + c[4] * Math.pow(lum, 3);
		return Math.round(power * 100)/100;
	}

	/**
	 * Checks if the slider has a selected number.
	 *
	 * @return {*} - Returns true if the slider has a selected number, false otherwise.
	 * @memberof VerticalSlider
	 */
	isSliderSatisfied() {
		return this.selectedNumber !== null && this.selectedNumber;
	}

	/**
	 * Renders the component.
	 *
	 * @return {*} - Returns a LitElement template result.
	 * @memberof VerticalSlider
	 */
	render() {
		const luminousLabel = {
			en: 'Luminous flux',
			fr: 'Flux lumineux',
			de: 'Lichtstrom',
		};
		const powerLabel = {
			en: 'Power',
			fr: 'Puissance',
			de: 'Leistung',
		};
		const currentLabel = {
			en: 'Current',
			fr: 'Actuel',
			de: 'Stromstärke',
		};
		const descriptionText = {
			en: 'Irure eu non voluptate. Voluptate anim elit ipsum nullaexercitation. Elit qui tempor aliqua amet consequat ut.',
			fr: 'Irure eu non voluptate. Voluptate anim elit ipsum nullaexercitation. Elit qui tempor aliqua amet consequat ut.',
			de: 'Irure eu non voluptate. Voluptate anim elit ipsum nullaexercitation. Elit qui tempor aliqua amet consequat ut.',
		};
		const s = store.getState();
		return html`
			${when(this.isSatisfied(s) || true, () => html`
				<div>
					<div class="title">
						<p class="title">${this.title[this.lang]}</p>
					</div>
					<div class="range-slider-wrapper">
						<div class="dimm-level">
							<div class="arrow-box">
								<div class="number-display">
									<p class="selected-number-display">${this.selectedNumber}</p><span>lm</span>
								</div>
							</div>
							<div id="vertical-slider" class="slider-wrapper">
								<ul class="numbers">
									${this.renderNumbers()}
								</ul>
							</div>
						</div>
						<div class="description">
							<div class="data">
								<p>${luminousLabel[this.lang]}:<span> ${this.selectedNumber} lm</span></p>
								<p>${powerLabel[this.lang]}:<span> ${this.calculatePower(this.selectedNumber)} W</span></p>
								<p>${currentLabel[this.lang]}:<span> ${this.calculateCurrent(this.selectedNumber)} mA</span></p>
							</div>
							<div class="description-text">
								<p>${descriptionText[this.lang]}</p>
							</div>
						</div>
					</div>
				</div>
			`)}
		`;
	}
}
